import * as React from 'react';
import {xor, intersection} from 'lodash';
import {debounceAsync} from 'src/utils/function';


export type ApiLocation = {
  zipcode: string;
  location_name: string;
  latitude: number;
  longitude: number;
  distance: number | null | undefined;
};
export type Location = ApiLocation & {
  label: string;
  value: string;
};

export type ListType = 'inside' | 'outside';
export type LocationOperatorType = 'manual' | 'search';

const DEBOUNCE_TIMEOUT = 500; //debounce timeout for api calls on search
export const MIN_QUERY_LENGTH = 2; //length of query after which api call should be made
export const DEFAULT_ZIPCODE_RADIUS = 20;
export const locationOperatorOptions: Array<{
  label: string;
  value: LocationOperatorType;
}> = [
  {label: 'enter zip codes manually', value: 'manual'},
  {
    label: 'is within radius of a location',
    value: 'search',
  },
];
export const distanceOptions: Array<{
  label: string;
  value: ListType;
}> = [
  {label: 'inside radius', value: 'inside'},
  {
    label: 'outside radius',
    value: 'outside',
  },
];
export const getMapCenter = (
  source: Location,
): {
  lat: number;
  lng: number;
} => ({
  lat: source.latitude,
  lng: source.longitude,
});
export const milesToZoomLevels = {
  '20': 9,
  '40': 8,
  '60': 8,
  '80': 7,
  '100': 7,
  '120': 7,
} as const;
export const getZoom = (radius: number): number =>
  // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'number' can't be used to index type '{ readonly '20': 9; readonly '40': 8; readonly '60': 8; readonly '80': 7; readonly '100': 7; readonly '120': 7; }'.
  milesToZoomLevels[radius] || 10;

const createLocationIdentifierLabel = (location: ApiLocation) =>
  `${location.zipcode}${
    location.location_name ? ` (${location.location_name.trim()})` : ''
  }`;
export const ApiLocationToLocation = (
  apiLocations: ApiLocation[],
): Location[] =>
  apiLocations.map((apiLocation) => ({
    ...apiLocation,
    value: apiLocation.zipcode,
    label: createLocationIdentifierLabel(apiLocation),
  }));

export const milesToMeters = (distance: number): number => distance * 1609.344;

export const calculateInsideAndOutsideZipCodes = (
  matchingZipcodes: {
    [key: string]: Location;
  },
  value: string[],
): [string[], string[]] => {
  //value contains both inside and outside zipcodes
  const matchedZipcodeValues = Object.keys(matchingZipcodes);
  const insideZipcodeValues =
    matchedZipcodeValues.length > 0
      ? intersection(matchedZipcodeValues, value)
      : value;
  //preserve outside zip codes
  const outsideZipcodesValues = xor(value, insideZipcodeValues);
  return [insideZipcodeValues, outsideZipcodesValues];
};

//this hook lets you prepare async data to work with autocomplete
export function useAsyncAutocomplete<
  T extends {
    label: string;
    value: string;
  },
>(
  query: string,
  resolveOptions: (arg1: string) => Promise<T[]>,
  onSelectOption: (arg1: T) => unknown,
  debounceTimeout: number = DEBOUNCE_TIMEOUT,
): {
  query: string;
  isLoading: boolean;
  options: T[];
  error: unknown;
  onSelect: (arg1: string) => unknown;
} {
  //react state to manage async autocomplete
  const [isLoading, setIsLoading] = React.useState(false);
  const [options, setOptions] = React.useState([]);
  const [error, setError] = React.useState<any>(null);

  const debouncedResolveOptions = React.useMemo(
    () => debounceAsync(resolveOptions, debounceTimeout),
    [debounceTimeout],
  );

  //react effect to fetch options
  React.useEffect(() => {
    const fetchOptions = async () => {
      setIsLoading(true);
      try {
        const options = await debouncedResolveOptions(query);
        // @ts-expect-error - TS2345 - Argument of type 'T[]' is not assignable to parameter of type 'SetStateAction<never[]>'.
        setOptions(options);
      } catch (error: any) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };
    if (query) {
      fetchOptions();
    }
  }, [query]);

  const onSelect = (label: string) => {
    // @ts-expect-error - TS2339 - Property 'label' does not exist on type 'never'.
    const selectedOption = options.find((option) => option.label === label);
    if (selectedOption) {
      onSelectOption(selectedOption);
    }
  };

  return {query, isLoading, options, error, onSelect};
}
